// Real-time task synchronization via Server-Sent Events (SSE) and HTMX URL-morphing.

// Ephemeral client instance ID to suppress self-triggered mutation echoes.
const CLIENT_ID = (typeof crypto !== 'undefined' && crypto.randomUUID) 
  ? crypto.randomUUID() 
  : 'client_' + Math.random().toString(36).substring(2) + Date.now().toString(36);

// Automatically stamp all outgoing HTMX requests with X-Client-ID
document.addEventListener('htmx:configRequest', (evt) => {
  if (evt.detail && evt.detail.headers) {
    evt.detail.headers['X-Client-ID'] = CLIENT_ID;
  }
});

let eventSource = null;
let reconnectTimer = null;

export function initRealtimeEvents() {
  // Only connect if the user is on an authenticated app view
  const stream = document.getElementById('task-stream') || document.querySelector('[data-siro-el="MyTasksStream"]') || document.querySelector('.task-app');
  if (!stream) {
    return;
  }

  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }

  try {
    eventSource = new EventSource('/events');

    eventSource.addEventListener('tasks-updated', (e) => {
      try {
        const data = JSON.parse(e.data);

        // Echo suppression: Ignore events triggered by this client instance
        if (data.actor_client_id && data.actor_client_id === CLIENT_ID) {
          return;
        }

        // Check active workspace match if current workspace is known
        const activeWsEl = document.querySelector('[data-workspace-id]');
        const currentWsId = activeWsEl?.getAttribute('data-workspace-id');
        if (data.workspace_id && currentWsId && data.workspace_id !== currentWsId) {
          return;
        }

        // If user has an open modal, drawer, or dialog, do not disrupt active interaction
        const openModal = document.querySelector('dialog[open], .modal:not(.hidden)');
        if (openModal) {
          return;
        }

        triggerTaskStreamMorph();
      } catch (err) {
        console.warn('[SSE] Error processing tasks-updated event:', err);
      }
    });

    eventSource.onerror = () => {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(initRealtimeEvents, 3000);
    };
  } catch (err) {
    console.warn('[SSE] EventSource init failed:', err);
  }
}

export function triggerTaskStreamMorph() {
  const stream = document.getElementById('task-stream') || document.querySelector('[data-siro-el="MyTasksStream"]');
  if (!stream || !window.htmx) return;

  const targetSelector = stream.id ? `#${stream.id}` : '[data-siro-el="MyTasksStream"]';

  // Request the exact current URL (preserving active filters, search query, and list)
  window.htmx.ajax('GET', window.location.href, {
    target: targetSelector,
    select: targetSelector,
    swap: 'outerMorph',
  });
}

// Window focus & visibility recovery: re-establish connection if dropped
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    if (!eventSource || eventSource.readyState === EventSource.CLOSED) {
      initRealtimeEvents();
    }
  }
});

window.addEventListener('focus', () => {
  if (!eventSource || eventSource.readyState === EventSource.CLOSED) {
    initRealtimeEvents();
  }
});

// Initialize on DOM load and after full HTMX swaps
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRealtimeEvents);
} else {
  initRealtimeEvents();
}

document.addEventListener('htmx:afterSwap', (evt) => {
  if (evt.target === document.body && (!eventSource || eventSource.readyState === EventSource.CLOSED)) {
    initRealtimeEvents();
  }
});
